import React, { FC, useEffect, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import Modal, { ModalProps } from 'components/common/modal/Modal';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import RefreshIcon from 'components/common/icons/RefreshIcon';
import AIBetaPill from 'components/common/AIModals/AIBetaPill';
import {
  OPENAI_FEEDBACK_INSIGHTS_VAR,
  OPENAI_GENERATE_FEEDBACK_INSIGHTS
} from 'graphql/queries/openai/openai.query';
import {
  UPSERT_FEEDBACK_SUMMARY_MUTATION,
  UPSERT_FEEDBACK_SUMMARY_RESULT,
  UPSERT_FEEDBACK_SUMMARY_VAR
} from 'graphql/mutations/opeanai/openai.mutation';

export interface GenerateFeedbackInsightModalProps extends ModalProps {
  questionId: string;
  questionTitle: string;
  eventId: string;
  refetchList: () => void;
  showToastError: (e: ApolloError) => void;
}

export const GenerateFeedbackInsightModal: FC<
  React.PropsWithChildren<GenerateFeedbackInsightModalProps>
> = ({
  questionId,
  questionTitle,
  eventId,
  refetchList,
  onRequestClose,
  showToastError,
  ...props
}) => {
  const [insightSummary, setInsightSummary] = useState<string>('');
  const [loadingText, setLoadingText] = useState('Sorting through responses...');
  const { loading, data, refetch } = useQuery<
    { generateFeedbackInsights: { answer: string } },
    OPENAI_FEEDBACK_INSIGHTS_VAR
  >(OPENAI_GENERATE_FEEDBACK_INSIGHTS, {
    variables: {
      questionId,
      eventId
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onError: (e) => {
      showToastError(e);
    }
  });

  const [upsertFeedbackSummary] = useMutation<
    UPSERT_FEEDBACK_SUMMARY_RESULT,
    UPSERT_FEEDBACK_SUMMARY_VAR
  >(UPSERT_FEEDBACK_SUMMARY_MUTATION);

  useEffect(() => {
    if (data?.generateFeedbackInsights?.answer) {
      setInsightSummary(data.generateFeedbackInsights.answer);
    }
  }, [data]);

  const handleSummarySubmit = async (): Promise<void> => {
    await upsertFeedbackSummary({
      variables: {
        eventId,
        questionId,
        summary: insightSummary
      }
    });
    // Reset state values
    setInsightSummary('');

    // Refetch question response data.
    refetchList();

    // Close modal
    onRequestClose();
  };

  return (
    <Modal
      headerText="Summarize feedback"
      showCloseButton
      leftHeaderComponent={<AIBetaPill />}
      primaryActionLabel="Save"
      showPrimaryAction
      handlePrimaryAction={(): void => {
        handleSummarySubmit();
      }}
      primaryActionDisabled={!insightSummary}
      showSecondaryAction
      secondaryActionLabelIcon={<RefreshIcon />}
      secondaryActionLabel="Try again"
      secondaryActionDisabled={!insightSummary}
      handleSecondaryAction={async (): Promise<void> => {
        setLoadingText('Sorting through more responses...');
        setInsightSummary('');
        refetch();
      }}
      onRequestClose={onRequestClose}
      maxWidth="556px"
      maxHeight="90vh"
      {...props}
    >
      <div className="tw--mt-4 tw-text-sm tw-font-normal tw-text-primary-grey">{questionTitle}</div>
      <div className="tw-mt-3 tw-flex tw-h-full tw-min-h-[120px] tw-overflow-y-scroll tw-rounded-lg tw-border-solid tw-border-border-grey tw-p-4">
        {!loading ? (
          <div dangerouslySetInnerHTML={{ __html: insightSummary }} />
        ) : (
          <div className="tw-ml-[auto] tw-mr-[auto] tw-flex tw-flex-col tw-items-center tw-justify-center">
            <LoadingSpinner />
            <div className="tw-text-medium-grey">{loadingText}</div>
          </div>
        )}
      </div>

      <div className="tw-mt-4 tw-text-xs tw-font-normal tw-text-primary-grey">
        AI analyzes up to 100 random responses to generate common themes. The output may not be
        fully accurate.&nbsp;
        <a
          className="tw-text-xs tw-font-normal tw-text-primary-grey tw-underline"
          href="https://help.epochapp.com/articles/ai-tools"
          target="_blank"
          rel="noreferrer"
        >
          Learn more
        </a>
      </div>
    </Modal>
  );
};
